import { useState, useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, Star, ShoppingCart, Heart, ChevronDown, Grid, List, ChevronLeft, ChevronRight, X, Smartphone, Headphones, Watch, Monitor, Package, Tablet } from 'lucide-react';
import styled from 'styled-components';
import { useCart } from './Cart';
import { useNavigate } from 'react-router-dom';
import productService from '../services/productService';

// Fonction utilitaire pour les images par défaut
const getDefaultImage = (product) => {
  // Utiliser la première image de la galerie si disponible
  if (product.image) {
    return `https://inza-backend.sajholding.org${product.image}`;
  }
  
  // Fallback vers une image par défaut locale
  return '/api/placeholder/300/300';
};

// Fonction pour obtenir l'icône 3D selon la catégorie
const getCategoryIcon = (categoryName) => {
  if (!categoryName || typeof categoryName !== 'string') {
    return Package;
  }
  
  const name = categoryName.toLowerCase();
  switch (name) {
    case 'iphone':
    case 'téléphone':
      return Smartphone;
    case 'accessoires':
    case 'écouteurs':
      return Headphones;
    case 'montres':
    case 'apple watch':
      return Watch;
    case 'mac':
    case 'ordinateur':
      return Monitor;
    case 'ipad':
    case 'tablette':
      return Tablet;
    default:
      return Package;
  }
};

// Fonctions utilitaires pour le filtre de prix
const isPricePresetActive = (preset, priceRange) => {
  const minMatch = !preset.min || (priceRange.min && parseInt(priceRange.min) === preset.min);
  const maxMatch = !preset.max || (priceRange.max && parseInt(priceRange.max) === preset.max);
  return minMatch && maxMatch;
};

const applyPricePreset = (preset, setPriceRange) => {
  setPriceRange({
    min: preset.min || '',
    max: preset.max || ''
  });
};

// Composant Slider de prix personnalisé
const PriceSlider = ({ min, max, value, onChange }) => {
  const [isDragging, setIsDragging] = useState(null);
  const sliderRef = useRef(null);
  
  const minPrice = 0;
  const maxPrice = 2000000; // 2M FCFA max
  
  const getPercentage = (value) => ((value - minPrice) / (maxPrice - minPrice)) * 100;
  
  const handleMouseDown = (type) => (e) => {
    setIsDragging(type);
    e.preventDefault();
  };
  
  const handleMouseMove = (e) => {
    if (!isDragging || !sliderRef.current) return;
    
    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const newValue = Math.round((percentage / 100) * (maxPrice - minPrice) + minPrice);
    
    onChange({
      ...value,
      [isDragging]: newValue
    });
  };
  
  const handleMouseUp = () => {
    setIsDragging(null);
  };
  
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, value]);
  
  const minPercent = getPercentage(value.min || minPrice);
  const maxPercent = getPercentage(value.max || maxPrice);
  
  return (
    <div className="price-slider-container">
      <div className="price-display">
        <span className="price-value min">{(value.min || 0).toLocaleString()} FCFA</span>
        <span className="price-range-text">Plage de prix</span>
        <span className="price-value max">{(value.max || maxPrice).toLocaleString()} FCFA</span>
      </div>
      
      <div className="slider-wrapper" ref={sliderRef}>
        <div className="slider-track">
          <div 
            className="slider-range"
            style={{
              left: `${minPercent}%`,
              width: `${maxPercent - minPercent}%`
            }}
          />
        </div>
        
        <div
          className={`slider-thumb ${isDragging === 'min' ? 'dragging' : ''}`}
          style={{ left: `${minPercent}%` }}
          onMouseDown={handleMouseDown('min')}
        />
        
        <div
          className={`slider-thumb max-thumb ${isDragging === 'max' ? 'dragging' : ''}`}
          style={{ left: `${maxPercent}%` }}
          onMouseDown={handleMouseDown('max')}
        />
      </div>
      
      <div className="price-labels">
        <span className="price-label">0 FCFA</span>
        <span className="price-label">2M FCFA</span>
      </div>
    </div>
  );
};

// Styled Components
const Section = styled.section`
  padding: 5rem 1rem 2rem;
  background: #f8fafc;
  min-height: 100vh;
  
  @media (min-width: 768px) {
    padding: 5rem 2rem 2rem;
  }
  
  @media (min-width: 1024px) {
    padding: 5rem 4rem 2rem;
  }
`;

const BannerCarousel = styled.div`
  position: relative;
  width: 100vw;
  height: 400px;
  margin: -4rem calc(-50vw + 50%) 3rem;
  overflow: hidden;
  
  @media (max-width: 768px) {
    height: 300px;
    margin: -4rem calc(-50vw + 50%) 3rem;
  }
`;

const BannerSlide = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  padding-top: 2rem;
  background: ${props => props.background || 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)'};
  background-size: ${props => props.$backgroundSize || 'cover'};
  background-position: ${props => props.$backgroundPosition || 'center'};
  
  .slide-content {
    text-align: center;
    z-index: 2;
    position: relative;
    padding: 2rem;
    
    h2 {
      font-size: clamp(1.5rem, 4vw, 2.5rem);
      font-weight: 700;
      margin-bottom: 1rem;
      text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
    
    p {
      font-size: clamp(1rem, 2vw, 1.25rem);
      opacity: 0.95;
      max-width: 600px;
      margin: 0 auto 2rem;
      line-height: 1.6;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    }
    
    .slide-stats {
      display: flex;
      justify-content: center;
      gap: 3rem;
      margin-top: 2rem;
      
      @media (max-width: 768px) {
        flex-direction: column;
        gap: 1rem;
      }
      
      .stat {
        text-align: center;
        
        .stat-number {
          font-size: 2rem;
          font-weight: 800;
          margin-bottom: 0.25rem;
        }
        
        .stat-label {
          font-size: 0.9rem;
          opacity: 0.9;
        }
      }
    }
    
    .cta-button {
      background: white;
      color: #3b82f6;
      border: none;
      border-radius: 0.5rem;
      padding: 0.75rem 2rem;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.3s ease;
      display: inline-block;
      margin-top: 1rem;
      
      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
      }
    }
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.1));
    z-index: 1;
  }
`;

const BannerNavigation = styled.div`
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
  
  .dot {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.5);
    cursor: pointer;
    transition: all 0.3s ease;
    
    &.active {
      background: white;
      width: 1.5rem;
      border-radius: 0.25rem;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.8);
    }
  }
`;

const BannerArrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  color: white;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-50%) scale(1.1);
  }
  
  &.prev {
    left: 1rem;
  }
  
  &.next {
    right: 1rem;
  }
  
  @media (max-width: 768px) {
    width: 2.5rem;
    height: 2.5rem;
    
    &.prev {
      left: 0.5rem;
    }
    
    &.next {
      right: 0.5rem;
    }
  }
`;

const AdSpace = styled.a`
  display: block;
  text-decoration: none;
  color: inherit;
  background: url(/images/d8e8f911835ba4ad165334c30d620329.jpg);
  background-size: cover;
  background-position: center;
  border-radius: 1rem;
  padding: 2rem;
  margin-bottom: 2rem;
  color: white;
  position: relative;
  overflow: hidden;
  min-height: 160px;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  }
  
  &::before {
    content: 'PUB';
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: rgba(255, 255, 255, 0.2);
    padding: 0.25rem 0.5rem;
    border-radius: 0.25rem;
    font-size: 0.75rem;
    font-weight: 600;
    backdrop-filter: blur(10px);
    z-index: 3;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.2));
    z-index: 1;
  }
  
  .ad-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    position: relative;
    z-index: 2;
    
    @media (max-width: 768px) {
      flex-direction: column;
      text-align: center;
    }
    
    .ad-text {
      flex: 1;
      
      h3 {
        font-size: clamp(1.25rem, 3vw, 1.75rem);
        font-weight: 700;
        margin-bottom: 0.5rem;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
      }
      
      p {
        font-size: clamp(0.9rem, 2vw, 1.1rem);
        opacity: 0.95;
        margin-bottom: 1rem;
        line-height: 1.4;
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
      }
      
      .ad-button {
        background: white;
        color: #d97706;
        border: none;
        border-radius: 0.5rem;
        padding: 0.75rem 1.5rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.3s ease;
        display: inline-block;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);
        
        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.4);
        }
      }
    }
    
    .ad-visual {
      flex: 0 0 200px;
      height: 120px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 0.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 3rem;
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255, 255, 255, 0.2);
      
      @media (max-width: 768px) {
        width: 100%;
        max-width: 300px;
        height: 100px;
        font-size: 2.5rem;
      }
    }
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  
  h1 {
    font-size: clamp(2rem, 5vw, 3rem);
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  p {
    font-size: clamp(1rem, 2vw, 1.25rem);
    color: #6b7280;
    max-width: 48rem;
    margin: 0 auto;
  }
`;

const ControlsBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  flex: 1;
  max-width: 400px;
  
  .search-icon {
    color: #6b7280;
    margin-right: 0.5rem;
  }
  
  .search-input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 0.9rem;
    
    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const SearchSuggestions = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.5rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  z-index: 50;
  max-height: 300px;
  overflow-y: auto;
  
  .suggestion-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    cursor: pointer;
    transition: background 0.2s ease;
    border-bottom: 1px solid #f3f4f6;
    
    &:last-child {
      border-bottom: none;
    }
    
    &:hover {
      background: #f8fafc;
    }
    
    .suggestion-image {
      width: 40px;
      height: 40px;
      border-radius: 0.375rem;
      object-fit: cover;
      background: #f3f4f6;
    }
    
    .suggestion-info {
      flex: 1;
      
      .suggestion-name {
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 0.25rem;
      }
      
      .suggestion-meta {
        display: flex;
        align-items: center;
        gap: 1rem;
        font-size: 0.875rem;
        color: #6b7280;
        
        .category {
          text-transform: capitalize;
        }
        
        .price {
          font-weight: 600;
          color: #3b82f6;
        }
      }
    }
  }
  
  .no-results {
    padding: 1rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
  
  @media (max-width: 768px) {
    justify-content: space-between;
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  color: #374151;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f3f4f6;
    border-color: #d1d5db;
  }
`;

const ViewToggle = styled.div`
  display: flex;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  overflow: hidden;
  
  .view-btn {
    padding: 0.75rem;
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &.active {
      background: #3b82f6;
      color: white;
    }
    
    &:hover:not(.active) {
      background: #f3f4f6;
    }
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 250px 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const Sidebar = styled.div`
  display: none;
  
  @media (min-width: 1024px) {
    display: block;
  }
`;

const FilterSection = styled.div`
  background: white;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  
  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  .filter-group {
    margin-bottom: 1.5rem;
    padding: 0.75rem;
    border-radius: 0.75rem;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border: 1px solid #e2e8f0;
    transition: all 0.3s ease;
    
    &:hover {
      background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
      border-color: #3b82f6;
      transform: translateX(4px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
    }
    
    .filter-label {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.5rem;
      cursor: pointer;
      
      input[type="checkbox"] {
        accent-color: #3b82f6;
        width: 18px;
        height: 18px;
        cursor: pointer;
      }
      
      .category-label {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        flex: 1;
        color: #374151;
        font-size: 0.95rem;
        cursor: pointer;
        transition: all 0.3s ease;
        
        .category-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
          border-radius: 0.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          
          &:hover {
            background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
            transform: scale(1.1) rotateY(180deg);
          }
        }
        
        .category-text {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          
          .category-count {
            font-size: 0.8rem;
            color: #6b7280;
            font-weight: 500;
          }
        }
        
        &:hover {
          color: #3b82f6;
          
          .category-count {
            color: #3b82f6;
          }
        }
      }
    }
  }
`;

const PriceRange = styled.div`
  .price-presets {
    display: grid;
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    
    .price-preset {
      padding: 0.75rem;
      border: 2px solid #e5e7eb;
      border-radius: 0.75rem;
      background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
      font-size: 0.875rem;
      font-weight: 500;
      color: #374151;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        border-color: #3b82f6;
        background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15);
      }
      
      &.active {
        border-color: #3b82f6;
        background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
        color: white;
        box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
      }
    }
  }
  
  .price-slider-container {
    padding: 1.5rem 1rem;
    background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
    border-radius: 0.75rem;
    border: 1px solid #e2e8f0;
    
    .price-display {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
      
      .price-value {
        font-size: 1.125rem;
        font-weight: 700;
        color: #1f2937;
        
        &.min {
          color: #3b82f6;
        }
        
        &.max {
          color: #10b981;
        }
      }
      
      .price-range-text {
        font-size: 0.875rem;
        color: #6b7280;
        font-weight: 500;
      }
    }
    
    .slider-wrapper {
      position: relative;
      height: 40px;
      display: flex;
      align-items: center;
      
      .slider-track {
        position: absolute;
        width: 100%;
        height: 6px;
        background: #e5e7eb;
        border-radius: 3px;
        
        .slider-range {
          position: absolute;
          height: 100%;
          background: linear-gradient(90deg, #3b82f6 0%, #10b981 100%);
          border-radius: 3px;
          transition: all 0.3s ease;
        }
      }
      
      .slider-thumb {
        position: absolute;
        width: 20px;
        height: 20px;
        background: white;
        border: 3px solid #3b82f6;
        border-radius: 50%;
        cursor: grab;
        box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
        transition: all 0.3s ease;
        z-index: 2;
        
        &:hover {
          transform: scale(1.2);
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4);
        }
        
        &:active {
          cursor: grabbing;
          transform: scale(1.1);
        }
        
        &.max-thumb {
          border-color: #10b981;
          box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
          
          &:hover {
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
          }
        }
      }
    }
    
    .price-labels {
      display: flex;
      justify-content: space-between;
      margin-top: 0.75rem;
      
      .price-label {
        font-size: 0.75rem;
        font-weight: 600;
        color: #6b7280;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
    }
  }
`;

const ProductsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
  
  @media (max-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  &.list-view {
    grid-template-columns: 1fr;
    
    .product-card {
      display: flex;
      flex-direction: row;
      height: 200px;
      
      .product-image {
        width: 200px;
        height: 100%;
        border-radius: 0.75rem 0 0 0.75rem;
      }
      
      .product-info {
        flex: 1;
        padding: 1.5rem;
      }
    }
  }
  
  /* Animation d'apparition des produits */
  .product-card {
    animation: fadeInUp 0.6s ease-out forwards;
    opacity: 0;
    
    @for $i from 1 through 20 {
      &:nth-child(#{$i}) {
        animation-delay: #{$i * 0.1}s;
      }
    }
  }
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 2rem;
  margin-top: 3rem;
  padding: 1.5rem 0;
  border-top: 1px solid #e5e7eb;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
    text-align: center;
  }
  
  .pagination-info {
    color: #6b7280;
    font-size: 0.9rem;
    font-weight: 500;
    
    @media (max-width: 768px) {
      width: 100%;
      margin-bottom: 0.5rem;
    }
  }
  
  .pagination-controls {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    flex: 1;
    justify-content: center;
    
    @media (max-width: 768px) {
      flex-wrap: wrap;
      justify-content: center;
      width: 100%;
    }
  }
  
  .page-btn {
    min-width: 2.5rem;
    height: 2.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    color: #374151;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover:not(:disabled) {
      background: #f3f4f6;
      border-color: #d1d5db;
      transform: translateY(-1px);
    }
    
    &.active {
      background: #3b82f6;
      color: white;
      border-color: #3b82f6;
      box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    
    @media (max-width: 768px) {
      min-width: 2rem;
      height: 2rem;
      font-size: 0.875rem;
    }
  }
  
  .items-per-page {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #6b7280;
    font-size: 0.9rem;
    font-weight: 500;
    
    select {
      padding: 0.5rem 0.75rem;
      border: 1px solid #e5e7eb;
      border-radius: 0.375rem;
      background: white;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.2s ease;
      
      &:focus {
        outline: none;
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
      }
    }
    
    @media (max-width: 768px) {
      width: 100%;
      justify-content: center;
      margin-top: 0.5rem;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #6b7280;
  
  .empty-icon {
    font-size: 4rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
  
  .empty-title {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #374151;
  }
  
  .empty-message {
    font-size: 1rem;
    margin-bottom: 2rem;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .empty-action {
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 0.5rem;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    
    &:hover {
      background: #2563eb;
    }
  }
`;

const CartNotification = styled.div`
  position: fixed;
  top: 100px;
  right: 20px;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.75rem;
  padding: 1rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 1rem;
  min-width: 300px;
  transform: translateX(${props => props.$show ? '0' : '400px'});
  transition: transform 0.3s ease;
  
  @media (max-width: 768px) {
    right: 10px;
    left: 10px;
    min-width: auto;
    transform: translateX(${props => props.$show ? '0' : '100vw'});
  }
  
  .notification-icon {
    width: 3rem;
    height: 3rem;
    background: #10b981;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    flex-shrink: 0;
  }
  
  .notification-content {
    flex: 1;
    
    .notification-title {
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 0.25rem;
    }
    
    .notification-message {
      font-size: 0.875rem;
      color: #6b7280;
    }
  }
  
  .notification-close {
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.25rem;
    
    &:hover {
      color: #374151;
    }
  }
`;

const ProductCard = styled(motion.div)`
  background: white;
  border-radius: 0.75rem;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    border-color: #3b82f6;
  }
  
  .product-image {
    width: 100%;
    height: 250px;
    object-fit: cover;
    background: #f3f4f6;
    position: relative;
    overflow: hidden;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.3s ease;
    }
    
    &:hover img {
      transform: scale(1.05);
    }
    
    /* Badge de catÃ©gorie */
    &::before {
      content: attr(data-category);
      position: absolute;
      top: 0.75rem;
      left: 0.75rem;
      background: rgba(59, 130, 246, 0.9);
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 1rem;
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: capitalize;
      z-index: 1;
    }
    
    /* Badge de réduction */
    &[data-discount]:not([data-discount=""])::after {
      content: attr(data-discount);
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      background: #ef4444;
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 1rem;
      font-size: 0.75rem;
      font-weight: 600;
      z-index: 1;
      animation: slideIn 0.3s ease;
    }
    
    @keyframes slideIn {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }
  }
  
  .product-info {
    padding: 1.5rem;
    
    .product-category {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
      padding: 0.375rem 0.75rem;
      background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
      border: 1px solid #bae6fd;
      border-radius: 0.5rem;
      font-size: 0.75rem;
      font-weight: 600;
      color: #0369a1;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      transition: all 0.3s ease;
      
      &:hover {
        background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
        border-color: #93c5fd;
        transform: translateY(-1px);
      }
      
      svg {
        flex-shrink: 0;
      }
      
      span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
    
    .product-title {
      font-size: 1.1rem;
      font-weight: 600;
      color: #1f2937;
      margin-bottom: 0.5rem;
      line-height: 1.3;
    }
    
    .product-description {
      color: #6b7280;
      font-size: 0.875rem;
      margin-bottom: 1rem;
      line-height: 1.4;
    }
    
    .product-rating {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-bottom: 1rem;
      
      .stars {
        display: flex;
        gap: 0.125rem;
        
        .star {
          color: #fbbf24;
          font-size: 0.875rem;
        }
      }
      
      .rating-text {
        color: #6b7280;
        font-size: 0.875rem;
      }
    }
    
    .product-price {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 1rem;
      
      .price-info {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        
        .price-row {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          
          .price {
            font-size: 1.5rem;
            font-weight: 700;
            color: #1f2937;
          }
          
          .old-price {
            font-size: 1rem;
            color: #9ca3af;
            text-decoration: line-through;
          }
        }
        
        .stock-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.25rem;
          font-size: 0.75rem;
          font-weight: 600;
          padding: 0.25rem 0.5rem;
          border-radius: 0.25rem;
          margin-top: 0.25rem;
          
          &.in-stock {
            background: #d1fae5;
            color: #065f46;
          }
          
          &.low-stock {
            background: #fed7aa;
            color: #92400e;
          }
          
          &.out-stock {
            background: #fee2e2;
            color: #991b1b;
          }
        }
      }
    }
    
    .product-actions {
      display: flex;
      gap: 0.5rem;
      
      .btn-primary {
        flex: 1;
        background: #3b82f6;
        color: white;
        border: none;
        border-radius: 0.5rem;
        padding: 0.75rem;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
          background: #2563eb;
        }
      }
      
      .btn-secondary {
        background: #f3f4f6;
        border: 1px solid #e5e7eb;
        border-radius: 0.5rem;
        padding: 0.75rem;
        color: #6b7280;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover {
          background: #e5e7eb;
          color: #374151;
        }
      }
    }
  }
`;

const BoutiquePage = () => {
  const { addToCart: addToCartGlobal, cartItems, getCartCount } = useCart();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [sortBy, setSortBy] = useState('featured');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [likedItems, setLikedItems] = useState([]);
  const [showCartNotification, setShowCartNotification] = useState(false);
  const [notificationProduct, setNotificationProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  
  // Ã‰tats pour l'API
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les produits depuis l'API
  useEffect(() => {
    const loadProducts = async () => {
      try {
        setLoading(true);
        const data = await productService.getProducts();
        setProducts(data);
      } catch (err) {
        console.error('Error loading products:', err);
        setError('Impossible de charger les produits');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, []);

  const handleProductClick = (productSlug) => {
    navigate(`/product/${productSlug}`);
  };

  const bannerSlides = [
    {
      id: 1,
      title: 'Boutique Exclusive INZA',
      description: 'Découvrez nos produits Apple authentiques avec garantie et service client premium',
      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(147, 51, 234, 0.8)), url(/images/96800710a7485c7e8c62ae9b55a7645c.jpg)',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      stats: [
        { number: '100%', label: 'Authentique' },
        { number: '2ans', label: 'Garantie' },
        { number: '24/7', label: 'Support' }
      ],
      cta: 'Découvrir'
    }
  ];

  // Calculer les catégories dynamiquement à partir des produits
  const getCategoriesFromProducts = () => {
    const categoryMap = new Map();
    
    products.forEach(product => {
      const categoryName = product.category_name || product.category;
      if (categoryName) {
        const count = categoryMap.get(categoryName) || 0;
        categoryMap.set(categoryName, count + 1);
      }
    });
    
    // Convertir en tableau et ajouter des IDs
    return Array.from(categoryMap.entries()).map(([name, count]) => ({
      id: name.toLowerCase().replace(/\s+/g, '-'),
      name: name,
      count: count
    }));
  };
  
  const categories = getCategoriesFromProducts();

  // Load liked items from localStorage on mount
  useEffect(() => {
    const savedLikes = localStorage.getItem('inza_likes');
    
    if (savedLikes) {
      setLikedItems(JSON.parse(savedLikes));
    }
  }, []);

  // Lire les paramètres de recherche depuis l'URL
  const location = useLocation();
  
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const searchParam = urlParams.get('search');
    
    console.log('URL changée, search param:', searchParam);
    
    if (searchParam) {
      setSearchQuery(searchParam);
    }
  }, [location.search]);

  // Save liked items to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('inza_likes', JSON.stringify(likedItems));
  }, [likedItems]);

  const addToCartBoutique = (product) => {
    // Add default configuration for boutique products
    const configuration = {
      storage: '128GB',
      color: 'Noir'
    };
    
    const productToAdd = {
      ...product,
      ...configuration,
      originalPrice: product.oldPrice || product.price
    };
    
    addToCartGlobal(productToAdd, configuration);
    
    // Show notification
    setNotificationProduct(product);
    setShowCartNotification(true);
    
    // Hide notification after 3 seconds
    setTimeout(() => {
      setShowCartNotification(false);
    }, 3000);
  };

  const toggleLike = (productId) => {
    if (likedItems.includes(productId)) {
      setLikedItems(likedItems.filter(id => id !== productId));
    } else {
      setLikedItems([...likedItems, productId]);
    }
  };

  // Auto-play for banner carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, [bannerSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % bannerSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + bannerSlides.length) % bannerSlides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  // SystÃ¨me de recherche amÃ©liorÃ©
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const suggestions = products
        .filter(product => 
          product && (
            (product.name && product.name.toLowerCase().includes(query)) ||
            (product.description && product.description.toLowerCase().includes(query)) ||
            (product.keywords && Array.isArray(product.keywords) && product.keywords.some(keyword => keyword.toLowerCase().includes(query)))
          )
        )
        .map(product => ({
          id: product.id,
          name: product.name,
          category: product.category,
          price: product.price,
          image: product.image
        }))
        .slice(0, 5); // Limiter Ã  5 suggestions
      
      setSearchSuggestions(suggestions);
      setShowSuggestions(true);
    } else {
      setSearchSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchQuery]);

  const filteredProducts = products.filter(product => {
    if (!product) return false;
    
    const query = searchQuery ? searchQuery.toLowerCase() : '';
    console.log('Recherche en cours pour:', query, 'produit:', product.name, 'searchQuery:', searchQuery);
    
    let matchesSearch = true;
    
    if (searchQuery && searchQuery.trim()) {
      matchesSearch = 
        (product.name && product.name.toLowerCase().includes(query)) ||
        (product.description && product.description.toLowerCase().includes(query)) ||
        ((product.category_name || product.category) && (product.category_name || product.category).toLowerCase().includes(query)) ||
        (product.keywords && Array.isArray(product.keywords) && product.keywords.some(keyword => keyword.toLowerCase().includes(query)));
    }
    
    console.log('matchesSearch:', matchesSearch, 'pour produit:', product.name);
    
    const matchesCategory = selectedCategories.length === 0 || 
      selectedCategories.some(selectedId => {
        const selectedCategory = categories.find(cat => cat.id === selectedId);
        if (!selectedCategory) return false;
        
        // Normaliser les noms pour la comparaison
        const productCategory = (product.category_name || product.category || '').toLowerCase().trim();
        const selectedName = selectedCategory.name.toLowerCase().trim();
        
        return productCategory === selectedName;
      });
    const matchesPrice = (!priceRange.min || product.price >= parseInt(priceRange.min)) &&
                         (!priceRange.max || product.price <= parseInt(priceRange.max));
    
    const result = matchesSearch && matchesCategory && matchesPrice;
    console.log('Résultat final pour', product.name, ':', result);
    
    return result;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'rating':
        return b.rating - a.rating;
      case 'name':
        return a.name.localeCompare(b.name);
      default:
        return 0;
    }
  });

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(sortedProducts.length / itemsPerPage);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategories, priceRange, sortBy]);

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.name);
    setShowSuggestions(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };

  return (
    <Section>
      <Container>
        {/* Banner Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <BannerCarousel>
            <AnimatePresence mode="wait">
              <motion.div
                key={currentSlide}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
              >
                <BannerSlide 
                  background={bannerSlides[currentSlide].background}
                  $backgroundSize={bannerSlides[currentSlide].backgroundSize}
                  $backgroundPosition={bannerSlides[currentSlide].backgroundPosition}
                >
                  <div className="slide-content">
                    <h2>{bannerSlides[currentSlide].title}</h2>
                    <p>{bannerSlides[currentSlide].description}</p>
                    
                    <div className="slide-stats">
                      {bannerSlides[currentSlide].stats.map((stat, index) => (
                        <div key={index} className="stat">
                          <div className="stat-number">{stat.number}</div>
                          <div className="stat-label">{stat.label}</div>
                        </div>
                      ))}
                    </div>
                    
                    <button className="cta-button">
                      {bannerSlides[currentSlide].cta}
                    </button>
                  </div>
                </BannerSlide>
              </motion.div>
            </AnimatePresence>

            {/* Navigation Arrows */}
            <BannerArrow className="prev" onClick={prevSlide}>
              <ChevronLeft size={20} />
            </BannerArrow>
            <BannerArrow className="next" onClick={nextSlide}>
              <ChevronRight size={20} />
            </BannerArrow>

            {/* Dots Navigation */}
            <BannerNavigation>
              {bannerSlides.map((_, index) => (
                <div
                  key={index}
                  className={`dot ${index === currentSlide ? 'active' : ''}`}
                  onClick={() => goToSlide(index)}
                />
              ))}
            </BannerNavigation>
          </BannerCarousel>
        </motion.div>

        {/* Ad Space */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          <AdSpace 
          href="https://www.apple.com/fr/shop/accessories" 
          target="_blank" 
          rel="noopener noreferrer"
        >
            <div className="ad-content">
              <div className="ad-text">
                <h3>UNE OFFRE SPECIALE LIMITEE</h3>
                <p>Jusqu'à  -20% sur tous les accessoires Apple avec code: INZA20</p>
                <button className="ad-button">Profiter de l'offre</button>
              </div>
              <div className="ad-visual">
                <Smartphone size={80} color="#007AFF" />
              </div>
            </div>
          </AdSpace>
        </motion.div>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Header>
            <h1>Tous nos produits</h1>
            <p>Explorez notre sélection complète d'iPhones, montres et accessoires</p>
          </Header>
        </motion.div>

        {/* Controls Bar */}
        <ControlsBar>
          <SearchContainer>
            <Search className="search-icon" size={20} />
            <form onSubmit={handleSearchSubmit} style={{ display: 'flex', flex: 1 }}>
              <input
                type="text"
                className="search-input"
                placeholder="Rechercher un produit..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim() && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              />
            </form>
            
            {/* Search Suggestions */}
            {showSuggestions && (
              <SearchSuggestions>
                {searchSuggestions.length > 0 ? (
                  searchSuggestions.map(suggestion => (
                    <div 
                      key={suggestion.id} 
                      className="suggestion-item"
                      onClick={() => handleSuggestionClick(suggestion)}
                    >
                      <img
                        src={suggestion.image}
                        alt={suggestion.name}
                        className="suggestion-image"
                      />
                      <div className="suggestion-info">
                        <div className="suggestion-name">{suggestion.name}</div>
                        <div className="suggestion-meta">
                          <span className="category">{suggestion.category}</span>
                          <span className="price">{suggestion.price.toLocaleString()} FCFA</span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    Aucun résultat trouvé pour "{searchQuery}"
                  </div>
                )}
              </SearchSuggestions>
            )}
          </SearchContainer>

          <FilterControls>
            <FilterButton>
              <Filter size={16} />
              Filtres
            </FilterButton>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              style={{
                padding: '0.75rem 1rem',
                border: '1px solid #e5e7eb',
                borderRadius: '0.5rem',
                background: 'white',
                color: '#374151'
              }}
            >
              <option value="featured">Mis en avant</option>
              <option value="price-low">Prix croissant</option>
              <option value="price-high">Prix décroissant</option>
              <option value="rating">Meilleures notes</option>
              <option value="name">Nom A-Z</option>
            </select>

            <ViewToggle>
              <button
                className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                onClick={() => setViewMode('grid')}
              >
                <Grid size={16} />
              </button>
              <button
                className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                onClick={() => setViewMode('list')}
              >
                <List size={16} />
              </button>
            </ViewToggle>
          </FilterControls>
        </ControlsBar>

        {/* Main Content */}
        <MainContent>
          {/* Sidebar Filters */}
          <Sidebar>
            <FilterSection>
              <h3>Catégories</h3>
              {categories.map(category => {
                const IconComponent = getCategoryIcon(category.name);
                return (
                <motion.div 
                  key={category.id} 
                  className="filter-group"
                  whileHover={{ scale: 1.02, x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="filter-label">
                    <input
                      type="checkbox"
                      id={category.id}
                      checked={selectedCategories.includes(category.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedCategories([...selectedCategories, category.id]);
                        } else {
                          setSelectedCategories(selectedCategories.filter(cat => cat !== category.id));
                        }
                      }}
                    />
                    <label htmlFor={category.id} className="category-label">
                      <motion.div 
                        className="category-icon"
                        whileHover={{ rotateY: 360, scale: 1.1 }}
                        transition={{ duration: 0.6 }}
                      >
                        <IconComponent size={20} color={selectedCategories.includes(category.id) ? "#3b82f6" : "#6b7280"} />
                      </motion.div>
                      <span className="category-text">
                        {category.name}
                        <span className="category-count">({category.count})</span>
                      </span>
                    </label>
                  </div>
                </motion.div>
              );
            })}
            </FilterSection>

            <FilterSection>
              <h3>Prix</h3>
              <PriceRange>
                <PriceSlider 
                  value={priceRange}
                  onChange={setPriceRange}
                />
              </PriceRange>
            </FilterSection>
          </Sidebar>

          {/* Products Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {loading ? (
              <div style={{ textAlign: 'center', padding: '50px', gridColumn: '1 / -1' }}>
                <div>Chargement des produits...</div>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '50px', color: '#ff6b6b', gridColumn: '1 / -1' }}>
                <div>{error}</div>
              </div>
            ) : currentItems.length > 0 ? (
              <ProductsGrid className={viewMode === 'list' ? 'list-view' : ''}>
                {currentItems.map((product, index) => (
                <ProductCard 
                  key={product.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -4 }}
                  className={viewMode}
                  onClick={() => handleProductClick(product.slug)}
                >
                  <div className="product-image" 
                       data-category={product.category}
                       data-discount={product.oldPrice && product.oldPrice > product.price ? `-${Math.round((1 - product.price / product.oldPrice) * 100)}%` : ''}>
                    <img src={getDefaultImage(product)} alt={product.name} />
                  </div>
                  <div className="product-info">
                    <div className="product-category">
                    {(() => {
                      console.log('Product category_name:', product.category_name);
                      console.log('Product category:', product.category);
                      const CategoryIcon = getCategoryIcon(product.category_name || product.category);
                      const categoryName = product.category_name || product.category || 'Produit';
                      return (
                        <>
                          <CategoryIcon size={14} />
                          <span>{categoryName}</span>
                        </>
                      );
                    })()}
                  </div>
                    <h3>{product.name}</h3>
                    <p>{product.description}</p>
                    
                    <div className="product-rating">
                      <div className="stars">
                        {[1, 2, 3, 4, 5].map(star => (
                          <Star
                            key={star}
                            className="star"
                            fill={star <= Math.floor(product.rating) ? 'currentColor' : 'none'}
                          />
                        ))}
                      </div>
                      <span className="rating-text">
                        {product.rating} ({product.reviews} avis)
                      </span>
                    </div>
                    
                    <div className="product-price">
                      <div className="price-info">
                        <div className="price-row">
                          <span className="price">{product.price.toLocaleString()} FCFA</span>
                          {product.oldPrice && (
                            <span className="old-price">{product.oldPrice.toLocaleString()} FCFA</span>
                          )}
                        </div>
                        <div className={`stock-badge ${Math.random() > 0.8 ? 'low-stock' : 'in-stock'}`}>
                          {Math.random() > 0.8 ? 'âš ï¸ Stock limitÃ©' : 'âœ… En stock'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="product-actions" onClick={(e) => e.stopPropagation()}>
                      <button 
                        className="btn-primary"
                        onClick={() => addToCartBoutique(product)}
                      >
                        <ShoppingCart size={16} style={{ marginRight: '0.5rem' }} />
                        Ajouter au panier
                      </button>
                      <button 
                        className={`btn-secondary ${likedItems.includes(product.id) ? 'liked' : ''}`}
                        onClick={() => toggleLike(product.id)}
                        style={{
                          background: likedItems.includes(product.id) ? '#ef4444' : '#f3f4f6',
                          color: likedItems.includes(product.id) ? 'white' : '#6b7280',
                          border: likedItems.includes(product.id) ? '1px solid #ef4444' : '1px solid #e5e7eb'
                        }}
                      >
                        <Heart 
                          size={16} 
                          fill={likedItems.includes(product.id) ? 'currentColor' : 'none'}
                        />
                      </button>
                    </div>
                  </div>
                </ProductCard>
              ))}
            </ProductsGrid>
            ) : (
              <EmptyState>
                <div className="empty-icon">😒​</div>
                <div className="empty-title">Aucun produit trouvé</div>
                <div className="empty-message">
                  {searchQuery.trim() 
                    ? `Aucun produit ne correspond à  votre recherche "${searchQuery}"`
                    : 'Aucun produit ne correspond aux filtres sélectionnés'
                  }
                </div>
                <button 
                  className="empty-action"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCategories([]);
                    setPriceRange({ min: '', max: '' });
                    setSortBy('featured');
                  }}
                >
                  Réinitialiser les filtres
                </button>
              </EmptyState>
            )}
        </motion.div>
        </MainContent>
      </Container>

      {/* Pagination */}
      {sortedProducts.length > itemsPerPage && (
        <Container>
          <Pagination>
            <div className="pagination-info">
              Affichage de {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, sortedProducts.length)} sur {sortedProducts.length} produits
            </div>
              
              <div className="pagination-controls">
                <button 
                  className="page-btn"
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>
                
                {/* Page numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage < maxVisiblePages - 1) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        className={`page-btn ${i === currentPage ? 'active' : ''}`}
                        onClick={() => paginate(i)}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pages;
                })()}
                
                <button 
                  className="page-btn"
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
              
              <div className="items-per-page">
                <span>Produits par page:</span>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
              </div>
            </Pagination>
          </Container>
        )}
      
      {/* Cart Notification */}
      <CartNotification $show={showCartNotification}>
        <div className="notification-icon">
          <ShoppingCart size={20} />
        </div>
        <div className="notification-content">
          <div className="notification-title">Ajouté au panier!</div>
          <div className="notification-message">
            {notificationProduct?.name} a été ajouté à votre panier
          </div>
        </div>
        <button 
          className="notification-close"
          onClick={() => setShowCartNotification(false)}
        >
          <X size={16} />
        </button>
      </CartNotification>
    </Section>
  );
};

export default BoutiquePage;

